import styled from '@emotion/styled';
import { useLayoutEffect } from 'react';

import {
  IconButton,
  Typography,
  useAccordion,
} from '@innovamat/glimmer-components';

import { useSmartGuides } from '../../context';
import { groupConsecutiveTips } from '../../digital-guides/utils/steps';
import { GuideType } from '../../types/smart-guide';
import { CollapsablePart } from '../CollapsablePart';
import { ContentBlocks } from '../ContentBlocks/ContentBlocks';
import { Materials } from '../Materials';
import { Media } from '../Media';
import { PreprInnerHtml } from '../PreprInnerHtml';
import { GroupedStep } from '../step/grouped-step';
import { SupportExtensionStep } from '../support-extension-step';
import { VideoThemes } from '../VideoThemes';
import { RenderStep } from './RenderStep';
import useScroll from './useScroll';

const PartsWrapper = styled.div`
  a {
    color: ${({ theme }) => theme.tokens.color.specific.element.link.value};
    text-decoration: underline;
    ::after {
      content: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0_16466_4121)'%3E%3Cpath d='M11.0626 0.600037H7.46258C7.39555 0.599945 7.33006 0.620083 7.27466 0.657818C7.21926 0.695553 7.17654 0.749128 7.15208 0.811536C7.12651 0.87257 7.1197 0.93984 7.13252 1.00476C7.14534 1.06968 7.17722 1.12931 7.22408 1.17604L8.70008 2.62953L4.44759 6.81452C4.36378 6.89884 4.31673 7.01289 4.31673 7.13177C4.31673 7.25065 4.36378 7.36471 4.44759 7.44902C4.48926 7.49154 4.53896 7.52537 4.59381 7.54854C4.64865 7.57171 4.70755 7.58377 4.76709 7.58402C4.8845 7.58473 4.99755 7.53951 5.08209 7.45802L9.34808 3.25953L10.8286 4.71753C10.8597 4.74908 10.8969 4.77409 10.9378 4.79109C10.9788 4.80809 11.0227 4.81674 11.0671 4.81653C11.112 4.81667 11.1564 4.80747 11.1976 4.78953C11.2583 4.76367 11.31 4.72034 11.346 4.66506C11.3821 4.60978 11.4009 4.54502 11.4001 4.47903V0.937536C11.3989 0.848388 11.363 0.763219 11.2999 0.700177C11.2369 0.637134 11.1517 0.601202 11.0626 0.600037Z' fill='%2314A395'/%3E%3Cpath d='M8.92508 5.37903C8.80573 5.37903 8.69127 5.42644 8.60688 5.51083C8.52249 5.59522 8.47508 5.70968 8.47508 5.82902V10.5H1.5001V3.52503H6.16658C6.28593 3.52503 6.40039 3.47762 6.48478 3.39323C6.56917 3.30884 6.61658 3.19438 6.61658 3.07503C6.61658 2.95568 6.56917 2.84122 6.48478 2.75683C6.40039 2.67244 6.28593 2.62503 6.16658 2.62503H1.4416C1.21842 2.62503 1.00438 2.71369 0.846567 2.8715C0.688755 3.02931 0.600098 3.24335 0.600098 3.46653V10.554C0.600094 10.7776 0.688608 10.9921 0.846292 11.1506C1.00398 11.3092 1.218 11.3988 1.4416 11.4H8.52908C8.75345 11.4 8.96863 11.3109 9.12729 11.1522C9.28594 10.9936 9.37508 10.7784 9.37508 10.554V5.82902C9.37508 5.70968 9.32767 5.59522 9.24327 5.51083C9.15888 5.42644 9.04442 5.37903 8.92508 5.37903Z' fill='%2314A395'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_16466_4121'%3E%3Crect width='12' height='12' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E%0A");
      margin-left: 4px;
    }

    :hover {
      cursor: pointer;
      color: ${({ theme }) =>
        theme.tokens.color.specific.element['link-hovered'].value};

      ::after {
        content: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0_16344_7967)'%3E%3Cpath d='M11.0626 0.600037H7.46258C7.39555 0.599945 7.33006 0.620083 7.27466 0.657818C7.21926 0.695553 7.17654 0.749128 7.15208 0.811536C7.12651 0.87257 7.1197 0.93984 7.13252 1.00476C7.14534 1.06968 7.17722 1.12931 7.22408 1.17604L8.70008 2.62953L4.44759 6.81452C4.36378 6.89884 4.31673 7.01289 4.31673 7.13177C4.31673 7.25065 4.36378 7.36471 4.44759 7.44902C4.48926 7.49154 4.53896 7.52537 4.59381 7.54854C4.64865 7.57171 4.70755 7.58377 4.76709 7.58402C4.8845 7.58473 4.99755 7.53951 5.08209 7.45802L9.34808 3.25953L10.8286 4.71753C10.8597 4.74908 10.8969 4.77409 10.9378 4.79109C10.9788 4.80809 11.0227 4.81674 11.0671 4.81653C11.112 4.81667 11.1564 4.80747 11.1976 4.78953C11.2583 4.76367 11.31 4.72034 11.346 4.66506C11.3821 4.60978 11.4009 4.54502 11.4001 4.47903V0.937536C11.3989 0.848388 11.363 0.763219 11.2999 0.700177C11.2369 0.637134 11.1517 0.601202 11.0626 0.600037Z' fill='%23108478'/%3E%3Cpath d='M8.92508 5.37903C8.80573 5.37903 8.69127 5.42644 8.60688 5.51083C8.52249 5.59522 8.47508 5.70968 8.47508 5.82902V10.5H1.5001V3.52503H6.16658C6.28593 3.52503 6.40039 3.47762 6.48478 3.39323C6.56917 3.30884 6.61658 3.19438 6.61658 3.07503C6.61658 2.95568 6.56917 2.84122 6.48478 2.75683C6.40039 2.67244 6.28593 2.62503 6.16658 2.62503H1.4416C1.21842 2.62503 1.00438 2.71369 0.846567 2.8715C0.688755 3.02931 0.600098 3.24335 0.600098 3.46653V10.554C0.600094 10.7776 0.688608 10.9921 0.846292 11.1506C1.00398 11.3092 1.218 11.3988 1.4416 11.4H8.52908C8.75345 11.4 8.96863 11.3109 9.12729 11.1522C9.28594 10.9936 9.37508 10.7784 9.37508 10.554V5.82902C9.37508 5.70968 9.32767 5.59522 9.24327 5.51083C9.15888 5.42644 9.04442 5.37903 8.92508 5.37903Z' fill='%23108478'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_16344_7967'%3E%3Crect width='12' height='12' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E%0A");
      }
    }
  }
`;

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const PartsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .tooltip-span {
    display: inline;
  }
`;

const partNumberToString = (partNumber: number) => {
  switch (partNumber) {
    case 0:
      return 'before-start';
    case 1:
      return 'warm-up';
    case 2:
      return 'activity';
    case 3:
      return 'log-it';
    default:
      return 'unknown';
  }
};

const StyledPart = styled.div`
  scroll-margin-top: calc(var(--heightHeader, 1vh) + 16px);
`;

const MaterialsContainer = styled.div`
  margin-bottom: 32px;
`;

export function Parts(): JSX.Element {
  const { smartGuide, t, urlPart, setEventData, session} =
    useSmartGuides();

  const { parts, summary } = smartGuide;
  const {
    isExpanded,
    onExpand,
    onExpandAll,
    onCollapseAll,
    areAllExpanded,
    areAllCollapsed,
  } = useAccordion({
    multiExpandable: true,
    length: parts.length,
  });

  const { setAccordionRef, onOpenAccordion } = useScroll({
    getIsOpen: isExpanded,
    itemsLength: parts.length,
  });

  const handleExpand = (index: number, value: boolean) => {
    const isOpen = isExpanded(index);
    const part = parts[index];

    if (part && typeof part.partNumber === 'number') {
      const action = value ? 'open' : 'close';
      const partName = partNumberToString(part.partNumber);
      const eventName = `${action}_${partName}_smartguide`;

      setEventData(eventName, {
        resource_id: session?.id,
        resource_name: session?.title,
        lesson_code: session?.code,
      });
    }

    onExpand(index, value);
    onOpenAccordion(index, isOpen);
  };

  useLayoutEffect(() => {
    const urlPartNumber = Number(urlPart);

    if (urlPartNumber < 0 || parts.length < urlPartNumber) return;
    const timeoutId = setTimeout(() => {
      handleExpand(urlPartNumber - 1, true);
    }, 500); // Arbitrary delay to wait for the accordion to be mounted and the scroll to be smooth

    return () => clearTimeout(timeoutId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPart, parts]);

  const isFourInOne =
    'guideType' in smartGuide && smartGuide.guideType === GuideType.FourInOne;

  return (
    <PartsWrapper>
      {isFourInOne && (
        <MaterialsContainer
          data-zone="materiales-inline"
          data-testid="materials"
        >
          <Materials {...summary.materials} />
        </MaterialsContainer>
      )}
      <Title>
        <Typography.H4>{t('digitalguides.session_preparation')}</Typography.H4>
        <div>
          <IconButton
            size="M"
            onClick={() => onExpandAll()}
            icon="ExpandAllIcon"
            tooltipText={t('smartguides.parts.expand-all') || ''}
            state={areAllExpanded ? 'disabled' : undefined}
            dataTestId="expand-all-button"
          />
          <IconButton
            size="M"
            onClick={() => onCollapseAll()}
            icon="CollapseAllIcon"
            tooltipText={t('smartguides.parts.collapse-all') || ''}
            state={areAllCollapsed ? 'disabled' : undefined}
            dataTestId="collapse-all-button"
          />
        </div>
      </Title>

      <PartsContainer>
        {parts.map((part, index: number) => {
          if (part.partNumber === 0) {
            return (
              <StyledPart
                key={index}
                ref={(el) => setAccordionRef(index, el)}
                data-part-type={partNumberToString(part.partNumber)}
              >
                <CollapsablePart
                  onExpand={(value) => handleExpand(index, value)}
                  isExpanded={isExpanded(index)}
                  part={part.partNumber}
                  momentTitle={t(part.title)}
                  activityTitle={t(part.oneLineSummary || '')}
                  dataTestId={`part-${partNumberToString(
                    part.partNumber
                  )}-${index}`}
                >
                  <Typography.Body1>
                    <PreprInnerHtml text={part.beforeStartContent?.content} />
                  </Typography.Body1>
                  {part.beforeStartContent?.media && (
                    <Media media={part.beforeStartContent?.media} />
                  )}
                </CollapsablePart>
              </StyledPart>
            );
          }

          if (part.id === 'smartGuidePartVideo') {
            return (
              <StyledPart
                key={index}
                ref={(el) => setAccordionRef(index, el)}
                data-part-type="video"
              >
                <CollapsablePart
                  onExpand={(value) => handleExpand(index, value)}
                  isExpanded={isExpanded(index)}
                  part={4}
                  momentTitle={t(part.title || '')}
                  activityTitle={t(part.chapterName || '')}
                  activityTitleExtra={`(${part.chapterDuration} min.)`}
                  dataTestId={`part-video-${index}`}
                >
                  <VideoThemes chapterContent={part.videoChapterContent} />
                </CollapsablePart>
              </StyledPart>
            );
          }

          return (
            <StyledPart
              key={index}
              ref={(el) => setAccordionRef(index, el)}
              data-part-type={partNumberToString(part.partNumber)}
            >
              <CollapsablePart
                onExpand={(value) => handleExpand(index, value)}
                isExpanded={isExpanded(index)}
                evaluable={part.evaluable}
                part={+part.partNumber}
                momentTitle={t(part.title, {
                  num: part.activityNumber,
                })}
                activityTitle={part.oneLineSummary || ''}
                isKeyMoment={part.keyMoment}
                partIcon={part.partIcon}
                dataTestId={`part-${partNumberToString(
                  part.partNumber
                )}-${index}`}
              >
                {part.contentBlocks && (
                  <ContentBlocks
                    part={+part.partNumber}
                    contentBlocks={part.contentBlocks}
                  />
                )}

                {(part.hasSupport || part.hasExtension) && (
                  <SupportExtensionStep
                    hasSupport={part.hasSupport}
                    hasExtension={part.hasExtension}
                    hasSupportLink={part.hasSupportLink}
                    hasExtensionLink={part.hasExtensionLink}
                    partNumber={+part.partNumber}
                  />
                )}
                {groupConsecutiveTips(part.steps).map(
                  (group, index: number) => (
                    <GroupedStep key={index} typename={group.__typename}>
                      {group.steps.map((step, index: number) => (
                        <RenderStep
                          step={step}
                          index={index}
                          partNumber={+part.partNumber}
                        />
                      ))}
                    </GroupedStep>
                  )
                )}
              </CollapsablePart>
            </StyledPart>
          );
        })}
      </PartsContainer>
    </PartsWrapper>
  );
}
